import React from 'react';
import { Star, Clock, User } from 'lucide-react';
import { format } from 'date-fns';

interface RecipePreviewProps {
  recipe: {
    title: string;
    ingredients: {
      name: string;
      amount: string;
      unit: string;
      base_ingredient: string;
    }[];
    instructions: string[];
    source_url: string;
    image_url?: string | null;
    rating?: {
      value: number;
      count: number;
      source: string;
    };
    reviews?: {
      text: string;
      author: string;
      rating: number;
      date: string;
    }[];
  } | null;
  loading?: boolean;
  error?: string | null;
}

function formatRatingStars(rating: number) {
  const fullStars = '★'.repeat(Math.floor(rating));
  const halfStar = rating % 1 >= 0.5 ? '½' : '';
  const emptyStars = '☆'.repeat(5 - Math.ceil(rating));
  return fullStars + halfStar + emptyStars;
}

export function RecipePreview({ recipe, loading, error }: RecipePreviewProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px] text-gray-500">
        <div className="animate-pulse">Loading preview...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-6 text-center">
        <div className="text-red-600 mb-4">{error}</div>
        <a 
          href={recipe?.source_url} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          Open recipe in new tab
        </a>
      </div>
    );
  }

  if (!recipe) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] p-6 text-center text-gray-500">
        <h3 className="text-xl font-medium mb-4">How to Import a Recipe</h3>
        <div className="space-y-6 max-w-md">
          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              1
            </div>
            <div className="text-left">
              <h4 className="font-medium mb-2">Search for a Recipe</h4>
              <p className="text-gray-600">
                Use the search box above to find recipes on Google. This will open in a new tab.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              2
            </div>
            <div className="text-left">
              <h4 className="font-medium mb-2">Copy the Recipe URL</h4>
              <p className="text-gray-600">
                When you find a recipe you like, copy its URL from your browser's address bar.
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
              3
            </div>
            <div className="text-left">
              <h4 className="font-medium mb-2">Preview and Import</h4>
              <p className="text-gray-600">
                Paste the URL in the import box and click preview. If it looks good, click Import!
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 overflow-y-auto max-h-[600px]">
      <div className="space-y-4">
        <h1 className="text-2xl font-bold">{recipe.title}</h1>
        
        {recipe.rating && (
          <div className="space-y-1">
            <div className="text-xl text-yellow-500">{formatRatingStars(recipe.rating.value)}</div>
            <div className="text-sm text-gray-600">
              {recipe.rating.value.toFixed(1)} out of 5 ({recipe.rating.count} {recipe.rating.count === 1 ? 'review' : 'reviews'})
              {recipe.rating.source && ` • ${recipe.rating.source}`}
            </div>
          </div>
        )}
        
        {recipe.image_url && (
          <img 
            src={recipe.image_url} 
            alt={recipe.title}
            className="w-full h-48 object-cover rounded-lg"
          />
        )}
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-3">Ingredients</h2>
        <ul className="space-y-2">
          {recipe.ingredients.map((ing, idx) => (
            <li key={idx} className="text-gray-700 border-b border-gray-100 pb-2">
              {ing.name}
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h2 className="text-lg font-semibold mb-3">Instructions</h2>
        <ol className="space-y-4 list-decimal list-inside">
          {recipe.instructions.map((step, idx) => (
            <li key={idx} className="text-gray-700">
              {step}
            </li>
          ))}
        </ol>
      </div>

      {recipe.reviews && recipe.reviews.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-3">Reviews</h2>
          <div className="space-y-4">
            {recipe.reviews.slice(0, 3).map((review, idx) => (
              <div key={idx} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="space-y-1">
                    <div className="text-yellow-500">{formatRatingStars(review.rating)}</div>
                    <div className="font-medium">{review.author}</div>
                  </div>
                  <div className="text-sm text-gray-500">
                    {format(new Date(review.date), 'MMM d, yyyy')}
                  </div>
                </div>
                <p className="text-gray-700">{review.text}</p>
              </div>
            ))}
            {recipe.reviews.length > 3 && (
              <div className="text-center">
                <a
                  href={recipe.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800"
                >
                  View all {recipe.reviews.length} reviews
                </a>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="pt-4 border-t border-gray-200 text-sm text-gray-500">
        Source:{' '}
        <a
          href={recipe.source_url}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800"
        >
          {recipe.source_url}
        </a>
      </div>
    </div>
  );
}